import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { DriverEvaluationConfirmRequestViewModel } from '../models/DriverEvaluationConfirmRequestViewModel';
import type { DriverEvaluationCreateRequestViewModel } from '../models/DriverEvaluationCreateRequestViewModel';
import type { DriverEvaluationCreateViewModel } from '../models/DriverEvaluationCreateViewModel';
import type { DriverEvaluationRenewCodeRequestViewModel } from '../models/DriverEvaluationRenewCodeRequestViewModel';
import type { DriverEvaluationRequestViewModel } from '../models/DriverEvaluationRequestViewModel';
import type { DriverEvaluationViewModel } from '../models/DriverEvaluationViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { VehicleListViewModel } from '../models/VehicleListViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class VehiclesService {
  /**
   * List
   * Gets all vehicles
   * @returns VehicleListViewModel Success
   * @throws ApiError
   */
  public static async listVehicles(
    {
      pickUpOffice,
      pickUpDateTime,
      deliveryOffice,
      deliveryDateTime,
      providers,
      priceMin,
      priceMax,
      fuelType,
      vehicleClass,
      transmissionType,
      doorCount,
      seatCount,
      luggageCount,
      sort,
      page,
      size,
    }: {
      pickUpOffice?: string;
      pickUpDateTime?: string;
      deliveryOffice?: string;
      deliveryDateTime?: string;
      providers?: Array<string>;
      priceMin?: number;
      priceMax?: number;
      fuelType?: Array<string>;
      vehicleClass?: Array<string>;
      transmissionType?: Array<string>;
      doorCount?: Array<number>;
      seatCount?: Array<number>;
      luggageCount?: Array<number>;
      sort?: any;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<VehicleListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<VehicleListViewModel>({
      method: 'GET',
      url: `/vehicles`,
      query: {
        ...(pickUpOffice ? { pickUpOffice: pickUpOffice } : {}),
        ...(pickUpDateTime ? { pickUpDateTime: pickUpDateTime } : {}),
        ...(deliveryOffice ? { deliveryOffice: deliveryOffice } : {}),
        ...(deliveryDateTime ? { deliveryDateTime: deliveryDateTime } : {}),
        ...(providers ? { providers: providers } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(fuelType ? { fuelType: fuelType } : {}),
        ...(vehicleClass ? { vehicleClass: vehicleClass } : {}),
        ...(transmissionType ? { transmissionType: transmissionType } : {}),
        ...(doorCount ? { doorCount: doorCount } : {}),
        ...(seatCount ? { seatCount: seatCount } : {}),
        ...(luggageCount ? { luggageCount: luggageCount } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Evaluation
   * Gets the evaluation of driver
   * @returns DriverEvaluationViewModel Success
   * @throws ApiError
   */
  public static async driverEvaluation(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      body?: DriverEvaluationRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<DriverEvaluationViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<DriverEvaluationViewModel>({
      method: 'POST',
      url: `/vehicles/evaluation`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Evaluation Create
   * Creates the evaluation of driver
   * @returns DriverEvaluationCreateViewModel Success
   * @throws ApiError
   */
  public static async driverEvaluationCreate(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      body?: DriverEvaluationCreateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<DriverEvaluationCreateViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<DriverEvaluationCreateViewModel>({
      method: 'POST',
      url: `/vehicles/evaluation-create`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Evaluation Confirm
   * Confirms evaluation of driver
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async driverEvaluationConfirm(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      body?: DriverEvaluationConfirmRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/vehicles/evaluation-confirm`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   *  Evaluation Confirm Renew Code
   * Renew Confirms Code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async driverEvaluationRenewConfirmCode(
    {
      basketId,
      body,
    }: {
      /**
       * Basket Id
       */
      basketId?: string;
      body?: DriverEvaluationRenewCodeRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/vehicles/evaluation-renew-code`,
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
